import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card } from "../components/ui/card";
import WithdrawInvest from "../components/WithdrawInvest";
import DepositInvest from "../components/DepositInvest";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Separator } from "./ui/separator";
import padlock from "../assets/icons/padlock.svg";
import securedBy80eight from "../assets/images/logoSmallGrey.svg";
import AuthTwoFA from "./AuthTwoFA";
import {
  createDeposit,
  createDepositQuote,
  createWithdraw,
  createWithdrawQuote,
} from "../services/investService";
import InvestWithdrawDepositOverview from "./InvestWithdrawDepositOverview";
import InvestWithdrawDepositComplete from "./InvestWithdrawDepositComplete";
import { useAuth } from "../context/AuthContext";
import {
  verify2FA,
  refreshPortfolioAndAssets,
} from "../services/digitalWalletService";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../hooks/use-toast";
import {
  useInvestmentBalance,
  useFeatureFlagsByCategory,
} from "../hooks/query/queries";
import { FeatureGate } from "./FeatureGate";
export default function InvestWithdraw({
  ledgerId,
  assetsOwned,
  ledgerProduct,
}) {
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures");
  const isWithdrawEnabled = featureFlags?.withdraw ?? true;
  const isDepositEnabled = featureFlags?.deposit ?? true;

  const getInitialTab = () => {
    if (!isWithdrawEnabled && isDepositEnabled) return "deposit";
    return "withdraw";
  };

  const [pageNo, setPageNo] = useState(0);
  const [twoFA, setTwoFA] = useState(false);
  const [selectedTab, setSelectedTab] = useState(getInitialTab());

  const [isVerifying, setIsVerifying] = useState(false);
  const [depositQuote, setDepositQuote] = useState(null);
  const [withdrawQuote, setWithdrawQuote] = useState(null);
  const [hasBeenSetUp, setHasBeenSetUp] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [showError, setShowError] = useState(false);
  const [parentAmount, setParentAmount] = useState(null);
  const [isQuoting, setIsQuoting] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [type, setType] = useState("withdraw");
  const handleTabChange = (value) => {
    setSelectedTab(value);
    setType(value);
  };
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { settings, updateSettings } = useAuth();
  const asset = ledgerProduct?.productId?.assetId?.asset;
  const productName = ledgerProduct?.productId?.name;
  const depositsEnabled = ledgerProduct?.productId?.isDepositsEnabled;
  const withdrawalsEnabled = ledgerProduct?.productId?.isWithdrawalsEnabled;

  useEffect(() => {
    setIsEnabled(settings?.security?.twoFactorAuth?.enabled);
  }, [settings]);

  useEffect(() => {
    if (!isWithdrawEnabled && isDepositEnabled && selectedTab === "withdraw") {
      setSelectedTab("deposit");
      setType("deposit");
    } else if (
      isWithdrawEnabled &&
      !isDepositEnabled &&
      selectedTab === "deposit"
    ) {
      setSelectedTab("withdraw");
      setType("withdraw");
    }
  }, [isWithdrawEnabled, isDepositEnabled, selectedTab]);

  const { data: amount, isLoading: loadingAmount } =
    useInvestmentBalance(ledgerId);
  const handleWithDrawTwoFA = () => {
    if (hasBeenSetUp) {
      if (type === "withdraw") {
        withdraw();
      }
      if (type === "deposit") {
        deposit();
      }
    }
    if (!hasBeenSetUp) {
      setTwoFA(true);
      setShowQuote(false);
    }
  };

  const depositData = {
    ledgerId: ledgerProduct?._id,
    amount: parentAmount,
  };
  const withdrawData = {
    amount: parentAmount,
    ledgerId: ledgerProduct?._id,
  };

  const depositArr = {
    amount: depositQuote?.amount,
    fee: 0,
    ledgerId: ledgerId,
  };
  const withdrawArr = {
    amount: withdrawQuote?.amount,
    ledgerId: ledgerId,
  };
  const handleVerify = async (token) => {
    try {
      const twoFAdata = {
        token: token,
      };
      const verifiedTwoFA = await verify2FA(twoFAdata);
      if (verifiedTwoFA) {
        updateSettings(verifiedTwoFA);
        toast({
          description: `2FA ${isEnabled ? "verified" : "enabled"} Successfully`,
          variant: "eighty",
        });
        if (type === "withdraw") {
          withdraw();
        }
        if (type === "deposit") {
          deposit();
        }
      }
    } catch (error) {
      toast({
        description: `${error}`,
        variant: "eighty",
      });
      setIsVerifying(false);
    }
  };
  const handleBackfromTwoFA = () => {
    setTwoFA(false);
    setShowQuote(true);
  };
  const deposit = async () => {
    try {
      const response = await createDeposit(depositArr);

      return response.data.data;
    } catch (error) {
      console.log("✔ Is Axios error?", axios.isAxiosError(error));

      toast({
        description: error,
        variant: "eighty",
      });

      setShowError(true);
    } finally {
      setIsVerifying(false);
      setTwoFA(false);
      setShowQuote(false);
      setShowComplete(true);
      queryClient.invalidateQueries({
        queryKey: ["investmentBalance"],
      });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      await refreshPortfolioAndAssets();
    }
  };
  const handleQuote = async () => {
    setIsQuoting(true);
    try {
      const response = await createDepositQuote(depositData);
      setDepositQuote(response.data.data);
      return response.data.data;
    } catch (error) {
      toast({
        description: `${error}`,
        variant: "eighty",
      });
    } finally {
      setPageNo(1);
      setShowQuote(true);
      setIsQuoting(false);
    }
  };
  const handleWithdrawQuote = async () => {
    setIsQuoting(true);
    try {
      const response = await createWithdrawQuote(withdrawData);

      setWithdrawQuote(response.data.data);

      return response.data.data;
    } catch (error) {
      toast({
        description: `${error}`,
        variant: "eighty",
      });
      setShowQuote(false);
      setIsQuoting(false);
    } finally {
      setPageNo(1);
      setShowQuote(true);
      setIsQuoting(false);
    }
  };
  const withdraw = async () => {
    try {
      const response = await createWithdraw(withdrawArr, ledgerId);
      return response.data.data;
    } catch (error) {
      toast({
        description: `${error}`,
        variant: "eighty",
      });
    } finally {
      setIsVerifying(false);
      setTwoFA(false);
      setShowQuote(false);
      setShowComplete(true);
      queryClient.invalidateQueries({
        queryKey: ["productTransactionHistory"],
      });
      queryClient.invalidateQueries({
        queryKey: ["investmentBalance"],
      });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      await refreshPortfolioAndAssets();
    }
  };
  const handleSetup = () => {
    setHasBeenSetUp(true);
  };
  const handleBackFromQuote = () => {
    setShowQuote(false);
    setPageNo(0);
    setIsQuoting(false);
  };
  return (
    <div className="col-span-4 px-8">
      <div className="py-4 mb-2">
        {!twoFA && (
          <Card className="bg-white rounded-2xl shadow-sm pt-10 pb-5  mb-4">
            {pageNo === 0 && (isWithdrawEnabled || isDepositEnabled) && (
              <div className="px-20">
                <Tabs onValueChange={handleTabChange} className="mb-8">
                  <TabsList
                    className={`grid ${isWithdrawEnabled && isDepositEnabled ? "grid-cols-2" : "grid-cols-1"} rounded-full bg-gray-100 h-full`}
                  >
                    <FeatureGate flag="withdraw">
                      <TabsTrigger
                        value="withdraw"
                        className={`py-3 text-lg font-semibold rounded-full transition-colors ${
                          selectedTab === "withdraw"
                            ? "bg-purple-900 text-white"
                            : "text-gray-600"
                        }`}
                      >
                        Withdraw
                      </TabsTrigger>
                    </FeatureGate>
                    <FeatureGate flag="deposit">
                      <TabsTrigger
                        value="deposit"
                        className={`py-3 text-lg font-semibold rounded-full transition-colors ${
                          selectedTab === "deposit"
                            ? "bg-purple-900 text-white"
                            : "text-gray-600"
                        }`}
                      >
                        Deposit
                      </TabsTrigger>
                    </FeatureGate>
                  </TabsList>
                </Tabs>
              </div>
            )}

            {!twoFA && selectedTab === "withdraw" && (
              <WithdrawInvest
                pageNo={pageNo}
                isQuoting={isQuoting}
                setPageNo={setPageNo}
                ledgerId={ledgerId}
                amount={amount}
                asset={asset}
                withdrawalsEnabled={withdrawalsEnabled}
                ledgerProduct={ledgerProduct}
                loadingAmount={loadingAmount}
                setParentAmount={setParentAmount}
                onDepositQuote={handleWithdrawQuote}
              />
            )}

            {!twoFA && selectedTab === "deposit" && (
              <DepositInvest
                pageNo={pageNo}
                setPageNo={setPageNo}
                isQuoting={isQuoting}
                ledgerId={ledgerId}
                amount={amount}
                asset={asset}
                depositsEnabled={depositsEnabled}
                assetsOwned={assetsOwned}
                setParentAmount={setParentAmount}
                onDepositQuote={handleQuote}
                ledgerProduct={ledgerProduct}
              />
            )}
            {showQuote && !twoFA && (
              <InvestWithdrawDepositOverview
                type={type}
                depositQuote={depositQuote}
                withdrawQuote={withdrawQuote}
                asset={asset}
                productName={productName}
                ledgerId={ledgerId}
                onConfirm={handleWithDrawTwoFA}
                setUp={handleSetup}
                onBack={handleBackFromQuote}
              />
            )}
            {showComplete && !twoFA && (
              <InvestWithdrawDepositComplete
                setPageNo={setPageNo}
                type={type}
                setShowComplete={setShowComplete}
              />
            )}
            {showError && !twoFA && (
              <InvestWithdrawDepositComplete
                setPageNo={setPageNo}
                setShowComplete={setShowComplete}
              />
            )}
            {pageNo < 2 && !twoFA && (
              <>
                <Separator />
                <div className="flex justify-center items-center pt-7 pb-4">
                  <img src={padlock} alt="" className="mr-2" />
                  <p className="text-[14px] text-text-secondary font-normal mr-2">
                    Secured by
                  </p>
                  <img src={securedBy80eight} alt="" />
                </div>
              </>
            )}
          </Card>
        )}
        {twoFA && (
          <AuthTwoFA
            onVerify={handleVerify}
            onBack={handleBackfromTwoFA}
            isVerifying={isVerifying}
            setIsVerifying={setIsVerifying}
          />
        )}
      </div>
    </div>
  );
}
